// src/components/clients/MarkInvoicePaidDialog.tsx
//
// PKT-0916 (A, reverse) · a client was just set to Payment Received while an
// invoice of theirs is still owed (sent / overdue). Ask "Mark INV-xxxx paid
// too?" — and when there are several, let the operator choose which. Yes goes
// through updateInvoiceStatus -> PATCH /invoices/{id}/status, the same route
// the invoice modal uses (authority check, paid_at, activity, dunning cancel,
// stage sync). "Not now" writes nothing.
//
// Why ask instead of doing it: the stage is a human's claim that money came
// in; the invoice is the record of WHICH money. A client can have more than
// one open invoice, and a stage click is not proof of which one was paid.
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { refusalMessage } from "../../lib/api";
import {
  fetchOpenInvoicesForClient,
  updateInvoiceStatus,
} from "../../services/invoiceService";
import type { Invoice, OpenInvoiceSummary } from "./invoiceTypes";

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const describeInvoice = (inv: OpenInvoiceSummary) =>
  [inv.invoice_number, money(inv.amount), inv.status, inv.property_name].filter(Boolean).join(" · ");

interface MarkInvoicePaidDialogProps {
  open: boolean;
  clientName: string;
  invoices: OpenInvoiceSummary[];
  onClose: () => void;
  onPaid?: (invoice: Invoice) => void;
}

const MarkInvoicePaidDialog: React.FC<MarkInvoicePaidDialogProps> = ({
  open,
  clientName,
  invoices,
  onClose,
  onPaid,
}) => {
  const single = invoices.length === 1;
  const [chosenId, setChosenId] = useState<string | null>(single ? invoices[0].id : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setChosenId(invoices.length === 1 ? invoices[0].id : null);
    setError(null);
  }, [invoices]);

  const confirm = async () => {
    if (!chosenId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateInvoiceStatus(chosenId, "paid");
      onPaid?.(updated);
      onClose();
    } catch (err: any) {
      setError(refusalMessage(err) || err?.message || "Could not mark the invoice paid.");
    } finally {
      setSaving(false);
    }
  };

  const title = single ? `Mark ${invoices[0].invoice_number} paid too?` : "Mark an invoice paid too?";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {clientName || "This client"} is now at Payment Received, but{" "}
          {single
            ? `${describeInvoice(invoices[0])} is still open.`
            : `${invoices.length} invoices are still open. Choose the one that was paid.`}
        </Typography>
        {!single && (
          <RadioGroup value={chosenId ?? ""} onChange={(e) => setChosenId(e.target.value)}>
            {invoices.map((inv) => (
              <FormControlLabel key={inv.id} value={inv.id} control={<Radio size="small" />} label={describeInvoice(inv)} />
            ))}
          </RadioGroup>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-testid="mark-invoice-paid-skip" onClick={onClose} disabled={saving}>
          Not now
        </Button>
        <Button
          data-testid="mark-invoice-paid-confirm"
          variant="contained"
          onClick={confirm}
          disabled={!chosenId || saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Mark paid
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * One offer path for every screen that moves a stage (Clients list/pipeline/
 * drawer and the Messages rail). Call `offer` AFTER the stage write succeeded
 * and only for a move INTO payment_received; render `dialog`.
 *
 * A failed invoice read shows nothing and logs: the stage move already
 * happened and is correct on its own; the prompt is a convenience, and an
 * invented "no open invoices" would be the only way to be wrong here.
 */
export function useMarkInvoicePaidPrompt(onPaid?: (invoice: Invoice) => void) {
  const [state, setState] = useState<{ clientName: string; invoices: OpenInvoiceSummary[] } | null>(null);

  const offer = useCallback(async (clientId: string, clientName: string) => {
    try {
      const invoices = await fetchOpenInvoicesForClient(clientId);
      if (invoices.length > 0) setState({ clientName, invoices });
    } catch (err) {
      console.error("mark-paid prompt: could not read open invoices", { clientId, err });
    }
  }, []);

  const dialog = (
    <MarkInvoicePaidDialog
      open={Boolean(state)}
      clientName={state?.clientName ?? ""}
      invoices={state?.invoices ?? []}
      onClose={() => setState(null)}
      onPaid={onPaid}
    />
  );

  return { offer, dialog };
}

export default MarkInvoicePaidDialog;
